import {api, RequestParam} from "@/shared/api";
import * as Input from "@/shared/utils/input";

import {BasicDataTable, BasicDataTableConfig, DataTablesDataSource, TypedColumnSettings} from "@/shared/ui/basicDataTable";
import {FieldObserver, FieldValueChangeEventInfo} from "@/shared/ui/fieldObserver";
import {BreakModelQueryParams} from "@/shared/queryParams";
import * as Constants from "@/shared/constants";
import {FormUtil} from "@/shared/forms/formUtil";
import {promiseNow} from "@/shared/utils/promise";
import {
	AssignmentPanel,
	AssignmentPanelComponent,
	AssignmentPanelConfiguration,
	BaseAssignmentTable
} from "@/shared/admin/assignmentPanel";
import * as BootstrapModal from "@/shared/ui/bootstrapModal";
import * as Button from "@/shared/utils/button";
import {
	copyOfObject,
	debounce,
	formatPercent,
	hoursToMillis,
	serializeObjectToArray,
	toggleNavigationTab,
	toggleNavigationTabs
} from "@/shared/utils/misc";
import {SecurityStatus} from "@/shared/securityStatus";
import {formatDuration, formatTime, getISOTime, parseDuration, parseTimeInput} from "@/shared/utils/datefunctions";
import {BreakModel, ErrorResponse, FieldError, FixedBreak, OvertimeLimit, WorkingTimeType} from "@/shared/timr";
import {
	AutomaticOvertimeCappingPeriod,
	CutHandlingMode,
	DurationUnit,
	FixedBreakType,
	FlexTimeIndicatorType,
	WeeklyRestPeriodStrategyType,
	WorkingTimeTypeCategory
} from "@/shared/timrTypes";
import {
	FixedBreakConfiguration,
	FixedBreakConfigurationPanelBridge
} from "@/features/admin/breakModel/fixedBreakConfigurationPanelBridge";
import {EMPTY_FIXED_BREAK, fixedBreakConfigurationsEqual} from "@/features/admin/breakModel/fixedBreakConfigurationModel";
import {
	clearInactiveFixedBreakFields,
	getFixedBreakRangeFields,
	isCutHandlingFieldError,
	isFixedBreakFieldError
} from "@/features/admin/breakModel/breakModelDialogHelper";
import {
	CutHandlingConfiguration,
	CutHandlingConfigurationPanelIslandCommands,
	CutHandlingConfigurationPanelIslandProps,
	mountCutHandlingConfigurationPanelIsland
} from "@/features/admin/breakModel/cutHandlingConfigurationPanelIsland";
import {ReactIsland} from "@/shared/reactIsland";
import {cutHandlingConfigurationsEqual} from "@/features/admin/breakModel/cutHandlingConfigurationModel";

/**
 * The weekday time-field prefixes of BreakModel (`flexTimeStart`, `coreTime1End`, …), derived from the
 * model itself: string-valued fields ending in a {@link Constants.Weekday}, with the weekday stripped.
 * Recombined with a weekday they name the per-day time fields (`coreTime1StartTuesday`, …).
 */
type TimeFieldPrefix = {
	[K in keyof BreakModel]-?: NonNullable<BreakModel[K]> extends string ? (K extends `${infer Prefix}${Constants.Weekday}` ? Prefix : never) : never
}[keyof BreakModel];

/** Runtime counterpart of the {@link TimeFieldPrefix} classifier: a Monday-suffixed key holding a string time value. */
function isMondayTimeFieldChange(data: FieldValueChangeEventInfo<BreakModel, keyof BreakModel>): data is FieldValueChangeEventInfo<BreakModel, `${TimeFieldPrefix}Monday`> {
	return data.field.endsWith("Monday") && typeof data.newValue === "string";
}

export class BreakModelsAdministrationPage {

	public static DEFAULT_MINIMUM_MANUAL_BREAK_DURATION = 15 * 60000; // 15 minutes
	public static DEFAULT_DAILY_REST_PERIOD_DURATION = 11 * 60 * 60000; // 11 hours

	private dataTable: BreakModelsTable;
	private breakModelDialogHandler: BreakModelsDialogHandler;

	constructor() {
		this.breakModelDialogHandler = new BreakModelsDialogHandler();

		const breakModelAddButton = $("#breakModelAddButton");
		breakModelAddButton.click(() => {
			this.breakModelDialogHandler.openDialog();
		});

		$(this.breakModelDialogHandler)
			.on(BreakModelsDialogHandler.EVENT_BREAK_MODEL_UPDATED, () => {
				this.dataTable.reload();
			})
			.on(BreakModelsDialogHandler.EVENT_BREAK_MODEL_DELETE, (event, breakModel: BreakModel) => {
				this.deleteBreakModel(breakModel);
			});

		this.dataTable = new BreakModelsTable({
			element: $("#breakModelsTable"),
			orderable: false,
			allowExpand: true,
			clampNote: true
		});

		$(this.dataTable)
			.on(BasicDataTable.EVENT_DBLCLICK + " " + BreakModelsTable.EVENT_BREAKMODEL_EDIT, (event, breakModel: BreakModel) => {
				this.breakModelDialogHandler.setBreakModel(breakModel);
				this.breakModelDialogHandler.openDialog();
			})
			.on(BreakModelsTable.EVENT_BREAKMODEL_DUPLICATE, (event, breakModel: BreakModel) => {
				this.breakModelDialogHandler.setBreakModel(breakModel, true);
				this.breakModelDialogHandler.openDialog();
			})
			.on(BreakModelsTable.EVENT_BREAKMODEL_DELETE, (event, breakModel: BreakModel) => {
				this.deleteBreakModel(breakModel);
			});

		// Execute initial action
		const initialAction = BreakModelQueryParams.getAnchor();
		if (initialAction === "addBreakModel") {
			breakModelAddButton.click();
			BreakModelQueryParams.clearAnchor();
		}
	}

	private deleteBreakModel(breakModel: BreakModel) {
		if (breakModel?.id) {
			BootstrapModal.confirm({
				title: "breakModels.context.delete",
				message: messages.withArguments("breakModels.confirm.delete", breakModel.name),
				yesLabel: "confirmation.delete",
				yesAction: (modal) => {
					api("admin/deleteBreakModel.json")
						.post({
							breakModelId: breakModel.id
						})
						.done(() => {
							this.breakModelDialogHandler.close();
							this.dataTable.reload();
						})
						.always(() => modal.close());
				},
				noLabel: "confirmation.cancel"
			});
		}
	}
}

class BreakModelsDialogHandler {
	public static EVENT_BREAK_MODEL_UPDATED = "eventModelUpdated";
	public static EVENT_BREAK_MODEL_DELETE = "eventModelDelete";

	private observer: FieldObserver<BreakModel>;

	private breakModelDialog: BootstrapModal.Modal;

	private breakModel: BreakModel;
	private originalBreakModel: BreakModel;

	private hasVariableBreakCheckboxChanged: boolean = false;

	private hasFixedBreakChanged: boolean = false;

	private overtimeRuleIds: number[];

	private hasOvertimeLimitChanged: boolean = false;

	private dayLabels = Constants.WEEKDAYS;

	private overtimeLimitPanel: OvertimeLimitPanel;

	private hasInitiallyCoreTime: boolean = false;

	private fixedBreakConfigurationPanel: FixedBreakConfigurationPanelBridge;

	private readonly cutHandlingConfigurationPanel?: ReactIsland<CutHandlingConfigurationPanelIslandProps, CutHandlingConfigurationPanelIslandCommands>;

	private validationState: FieldError[] = [];

	constructor() {
		this.breakModelDialog = new BootstrapModal.Modal({
			title: "breakModel.header.create",
			message: $("#breakModelDialog"),
			destroyOnClose: false,
			// Per-day fixed breaks render seven weekday columns of time inputs, so widen this dialog specifically.
			css: {dialog: "modal-break-model"},
			buttons: [{
				text: "dialog.save",
				css: "btn-primary",
				action: this.checkAndSaveBreakModel
			}, {
				text: "dialog.cancel"
			}, {
				text: "dialog.delete",
				css: "btn-outline-danger",
				left: true,
				action: () => {
					$(this).triggerHandler(BreakModelsDialogHandler.EVENT_BREAK_MODEL_DELETE, this.breakModel);
				}
			}],
			onOpen: () => {
				if (!this.breakModel.flexTimeEnabled && $("li#liFlexTimeTab").hasClass("active")) {
					FormUtil.switchTabNavigationToFirst($("ul.dialog-nav-tabs"));
				}
				this.hasInitiallyCoreTime = this.hasCoreTime();
				this.resizeAssignmentPanels();
				$("#breakModelName").focus();
			},
			onClose: () => {
				this.overtimeLimitPanel.setEditMode(false);
				this.resetBreakModel();
			}
		});

		this.observer = new FieldObserver<BreakModel>();
		this.observer.onFieldValueChange((e, data) => {
			// Don't update the field values if the dailyBreakConsiderExistingBreaks or dailyBreakHardCut fields have changed
			// because otherwise the UI can be collapsed and the user has to expand it again
			if (data.field === "dailyBreakConsiderExistingBreaks" || data.field === "dailyBreakHardCut") {
				return;
			}
			this.updateFieldValues();
		});

		const workingTimeTypesSource = api("admin/workingTimeTypes.json")
			.get({
				categories: [WorkingTimeTypeCategory.AttendanceTime],
				editUnit: DurationUnit.Minutes,
				archived: false
			})
			.then(response => response.data);

		this.overtimeLimitPanel = new OvertimeLimitPanel({
			panelElement: $("#overtimeLimitPanel"),
			assignedSourceProvider: () => {
				const breakModelId = this.breakModel.id !== null ? this.breakModel.id : this.originalBreakModel.id;

				if (breakModelId) {
					return api("admin/loadOvertimeLimits.json")
						.get({
							breakModelId: breakModelId
						})
						.then(data => data.overtimeLimits);
				} else {
					return promiseNow([]);
				}
			},
			selectSourceProvider: () => workingTimeTypesSource,
			editMessage: "breakModel.overtimeLimit.edit.start",
			saveMessage: "breakModel.overtimeLimit.edit.finish"
		}, () => {
			this.onDurationUpdated();
		});

		$('a[data-toggle="tab"]').on("shown.bs.tab", (e) => {
			const tabId = $(e.target).attr("href");
			if (tabId === "#tabOverTime") {
				this.overtimeLimitPanel.updateView();
			}
		});

		this.overtimeLimitPanel.onEditModeChanged(() => {
			const editMode = this.overtimeLimitPanel.isInEditMode();
			this.breakModelDialog.getButtons("").toggle(!editMode);
			toggleNavigationTabs("#tabNavigation", editMode);
		});

		this.fixedBreakConfigurationPanel = new FixedBreakConfigurationPanelBridge({
			container: document.getElementById("fixedBreakConfigurationPanel"),
			// DEVNOTE: Keep this as a push callback instead of pulling React state on save. The legacy
			// break-model dialog needs fixed-break dirty tracking and validation cleanup before save.
			onChange: this.onFixedBreakConfigurationChange
		});

		this.cutHandlingConfigurationPanel = mountCutHandlingConfigurationPanelIsland("cutHandlingConfigurationPanel", this.onCutHandlingConfigurationChange);

		$(window).resize(debounce(() => this.resizeAssignmentPanels(), this, 50));

		this.resetBreakModel();
		this.initListener();
		this.updateFieldValues();
		this.updateCutsTabAccess();
	}

	public openDialog() {
		this.hasVariableBreakCheckboxChanged = false;
		this.hasFixedBreakChanged = false;
		this.hasOvertimeLimitChanged = false;
		this.overtimeRuleIds = [];
		this.breakModelDialog.show();
	}

	public close() {
		this.breakModelDialog.close();
	}

	public resetBreakModel() {
		$(`#tabBreaks input[type="checkbox"]`).prop("checked", false);
		$(`#tabFlexiTime input[type="checkbox"]`).prop("checked", false);
		if (SecurityStatus.isOvertimeAllowanceAllowed()) {
			$(`#tabOvertimeRules input[type="checkbox"]`).prop("checked", false);
		}
		this.setBreakModel({
			id: null,
			version: 0,
			name: "",
			deductLegalMinimumBreaks: true,
			fullyDeductLegalMinimumBreaks: false,
			fixedBreakType: FixedBreakType.NONE,
			fixedBreak: {...EMPTY_FIXED_BREAK},
			fixedBreakMonday: {...EMPTY_FIXED_BREAK},
			fixedBreakTuesday: {...EMPTY_FIXED_BREAK},
			fixedBreakWednesday: {...EMPTY_FIXED_BREAK},
			fixedBreakThursday: {...EMPTY_FIXED_BREAK},
			fixedBreakFriday: {...EMPTY_FIXED_BREAK},
			fixedBreakSaturday: {...EMPTY_FIXED_BREAK},
			fixedBreakSunday: {...EMPTY_FIXED_BREAK},
			applyToProjectTime: false,
			minimumManualBreakDuration: BreakModelsAdministrationPage.DEFAULT_MINIMUM_MANUAL_BREAK_DURATION,
			validateDailyRestPeriod: false,
			validateHolidayRestPeriod: false,
			cutExceedingDaily: false,
			validateWeeklyRestPeriodStrategy: WeeklyRestPeriodStrategyType.NONE,
			validateDailyRestPeriodDuration: BreakModelsAdministrationPage.DEFAULT_DAILY_REST_PERIOD_DURATION,
			validateWeeklyRestPeriodStart: getISOTime(parseTimeInput("13:00")),
			validateHolidayRestPeriodStart: getISOTime(parseTimeInput("00:00")),
			cutHandlingMode: CutHandlingMode.VALIDATION,
			cutWarningThresholdDuration: null,
			automaticOvertimeCappingPeriod: AutomaticOvertimeCappingPeriod.NONE,
			automaticOvertimeCappingDuration: 0,
			flexTimeEnabled: false,
			flexTimeIndicatorType: FlexTimeIndicatorType.NOT_ACTIVE,
			flexTimeIndicatorYellowEmailAdministrator: false,
			flexTimeIndicatorYellowEmailTeamLeader: false,
			flexTimeIndicatorYellowEmailUser: false,
			flexTimeIndicatorRedEmailAdministrator: false,
			flexTimeIndicatorRedEmailTeamLeader: false,
			flexTimeIndicatorRedEmailUser: false,
		});
	}

	public setBreakModel(breakModel: BreakModel, duplicate = false) {
		this.breakModel = copyOfObject(breakModel);
		this.originalBreakModel = copyOfObject(breakModel);
		if (duplicate) {
			this.breakModel.id = null;
			this.breakModel.name += " " + messages["text.duplicate"];
			this.breakModel.version = 0;
		}
		this.observer.setModel(this.breakModel);
		this.updateTitle(duplicate);
		this.updateFieldValues();
		// After updateFieldValues, so the panel re-seeds from the loaded configuration.
		this.cutHandlingConfigurationPanel?.dispatchCommand("reset", null);
		this.updateCoreTimeCheckbox();
		this.overtimeLimitPanel.reloadAssigned();
		if (SecurityStatus.isOvertimeAllowanceAllowed()) {
			this.updateOvertimeRule(duplicate);
		}
		this.toggleFlexTimeMode(this.breakModel.flexTimeEnabled);
		this.updateApplyCutsToProjectTimeChangeableState();
		this.toggleAutomaticOvertimeCappingCheckbox();
		this.clearAllValidationFeedback();
		if (this.breakModel.flexTimeIndicatorType === FlexTimeIndicatorType.NOT_ACTIVE) {
			// Set default values here to avoid setting them every time the type is changed.
			// This prevents the user from losing his changes when changing back and forth without saving
			// Set default values for flex time indicators
			this.breakModel.flexTimeIndicatorPositiveRedThresholdDuration = hoursToMillis(50);
			this.breakModel.flexTimeIndicatorPositiveYellowThresholdDuration = hoursToMillis(25);
			this.breakModel.flexTimeIndicatorNegativeYellowThresholdDuration = null;
			this.breakModel.flexTimeIndicatorNegativeRedThresholdDuration = hoursToMillis(20);
			this.breakModel.flexTimeIndicatorPositiveRedThresholdPercentage = 1;
			this.breakModel.flexTimeIndicatorPositiveYellowThresholdPercentage = 0.8;
			this.breakModel.flexTimeIndicatorNegativeYellowThresholdPercentage = null;
			this.breakModel.flexTimeIndicatorNegativeRedThresholdPercentage = 0.2;
		}
	}

	/**
	 * Updates the visibility of the flex time lights state based on the current break model's flex time light type.
	 * It hides all flex time light containers and shows only the one that matches the current flex time light type.
	 */
	private updateFlexTimeIndicatorsState(): void {
		$("#flextimeIndicatorContainer > div").hide()
			.filter(`[id$=${this.breakModel.flexTimeIndicatorType}]`).show();
		$("#flexTimeIndicatorInfo > div").hide()
			.filter(`[id$=${this.breakModel.flexTimeIndicatorType}]`).show();
		$(`[id=greenThreshold_${this.breakModel.flexTimeIndicatorType}]`).text(
			messages.withArguments("breakModel.flextimeIndicator.greenThreshold",
				this.getMaxGreenThreshold(this.breakModel.flexTimeIndicatorType),
				this.getMinGreenThreshold(this.breakModel.flexTimeIndicatorType)
			)
		);
	}

	private updateOvertimeRule(duplicate: boolean): void {
		const breakModelId = duplicate ? this.originalBreakModel.id : this.breakModel.id;
		if (breakModelId) {
			const overtimeRulesList = $("#overtimeRuleList").find("[id^=overtimeRule]");
			api("admin/loadOvertimeRules.json")
				.get({
					breakModelId: breakModelId
				})
				.done(data => {
					for (const overtimeRule of data.overtimeRules) {
						overtimeRulesList.filter(`#overtimeRule${overtimeRule.id}`).prop("checked", true);
						this.overtimeRuleIds.push(overtimeRule.id);
					}
				});
		}
	}

	private onDurationUpdated() {
		this.hasOvertimeLimitChanged = true;
		this.overtimeLimitPanel.updateView();
	}

	private hasOvertimeRulesChanged(): boolean {
		if (!SecurityStatus.isOvertimeAllowanceAllowed()) {
			return false;
		}
		const checkedOvertimeRules: number[] = [];
		$("#overtimeRuleList input[type=checkbox]:checked").each((index, elem) => {
			checkedOvertimeRules.push(Number(elem.getAttribute("value")));
		});

		if (checkedOvertimeRules.length !== this.overtimeRuleIds.length) {
			return true;
		}
		this.overtimeRuleIds.sort((a, b) => (a - b));
		return !checkedOvertimeRules
			.sort((a, b) => (a - b))
			.every((v, i) => v === this.overtimeRuleIds[i]);
	}

	/**
	 * Checks for changes in the break model settings related to cuts in flex time, normal working time, and if the cut applies to project times
	 * @return true if there are changes in the cut settings for flex time, normal working time, or applies to project times otherwise, false
	 */
	private hasCutFlexTimeOrNormalWorkingTimeOrDailyChanged(): boolean {
		// check if cut before or after flex time has changed
		const hasCutFlexTimeChanged = this.breakModel.flexTimeEnabled &&
			(this.breakModel.cutBeforeFlexTimeStarts && (!this.originalBreakModel.cutBeforeFlexTimeStarts || !this.originalBreakModel.flexTimeEnabled) ||
				this.breakModel.cutAfterFlexTimeEnds && (!this.originalBreakModel.cutAfterFlexTimeEnds || !this.originalBreakModel.flexTimeEnabled));

		// check if cut before or after normal working time has changed
		const hasCutNormalWorkingTimeChanged = !this.breakModel.flexTimeEnabled &&
			(this.breakModel.cutBeforeNormalWorkingTimeStarts && (!this.originalBreakModel.cutBeforeNormalWorkingTimeStarts || this.originalBreakModel.flexTimeEnabled) ||
				this.breakModel.cutAfterNormalWorkingTimeEnds && (!this.originalBreakModel.cutAfterNormalWorkingTimeEnds || this.originalBreakModel.flexTimeEnabled));

		const hasCutFlexTimeOrNormalWorkingTimeChanged = hasCutFlexTimeChanged || hasCutNormalWorkingTimeChanged;

		// check if applying cuts to project time has changed
		const hasApplyCutsToProjectTimeChanged = this.breakModel.applyCutsToProjectTime && !this.originalBreakModel.applyCutsToProjectTime;

		// return true if any of the conditions for changes about cut conditions time are met
		return hasCutFlexTimeOrNormalWorkingTimeChanged || hasApplyCutsToProjectTimeChanged || (this.breakModel.applyCutsToProjectTime && hasCutFlexTimeOrNormalWorkingTimeChanged);
	}

	private isCutFlexTimeSet() {
		return this.breakModel.flexTimeEnabled && (this.breakModel.cutBeforeFlexTimeStarts || this.breakModel.cutAfterFlexTimeEnds);
	}

	private hasFlexTimeChanged() {
		return this.breakModel.flexTimeEnabled !== this.originalBreakModel.flexTimeEnabled ||
			this.breakModel.flexTimeStartMonday !== this.originalBreakModel.flexTimeStartMonday ||
			this.breakModel.flexTimeEndMonday !== this.originalBreakModel.flexTimeEndMonday ||
			this.breakModel.flexTimeStartTuesday !== this.originalBreakModel.flexTimeStartTuesday ||
			this.breakModel.flexTimeEndTuesday !== this.originalBreakModel.flexTimeEndTuesday ||
			this.breakModel.flexTimeStartWednesday !== this.originalBreakModel.flexTimeStartWednesday ||
			this.breakModel.flexTimeEndWednesday !== this.originalBreakModel.flexTimeEndWednesday ||
			this.breakModel.flexTimeStartThursday !== this.originalBreakModel.flexTimeStartThursday ||
			this.breakModel.flexTimeEndThursday !== this.originalBreakModel.flexTimeEndThursday ||
			this.breakModel.flexTimeStartFriday !== this.originalBreakModel.flexTimeStartFriday ||
			this.breakModel.flexTimeEndFriday !== this.originalBreakModel.flexTimeEndFriday ||
			this.breakModel.flexTimeStartSaturday !== this.originalBreakModel.flexTimeStartSaturday ||
			this.breakModel.flexTimeEndSaturday !== this.originalBreakModel.flexTimeEndSaturday ||
			this.breakModel.flexTimeStartSunday !== this.originalBreakModel.flexTimeStartSunday ||
			this.breakModel.flexTimeEndSunday !== this.originalBreakModel.flexTimeEndSunday;
	}

	private hasMaximumWorkDurationDailyChanged() {
		return this.breakModel.maximumWorkDurationDaily !== this.originalBreakModel.maximumWorkDurationDaily;
	}

	private hasCutExceedingDailyChanged() {
		return this.breakModel.cutExceedingDaily !== this.originalBreakModel.cutExceedingDaily;
	}

	private hasCutHandlingChanged() {
		return !cutHandlingConfigurationsEqual(this.getCutHandlingConfiguration(), {
			cutHandlingMode: this.originalBreakModel.cutHandlingMode,
			cutWarningThresholdDuration: this.originalBreakModel.cutWarningThresholdDuration
		});
	}

	private isCutNormalWorkingTimeSet() {
		return !this.breakModel.flexTimeEnabled && (this.breakModel.cutBeforeNormalWorkingTimeStarts || this.breakModel.cutAfterNormalWorkingTimeEnds);
	}

	/**
	 * Whether saving should offer to apply the cuts to the unbalanced recordings, which is only the case if a changed
	 * setting affects a cut that is active.
	 */
	private shouldAskToApplyCuts(): boolean {
		// the start/end cuts (flex time or normal working time) require the recording restrictions feature
		const isStartOrEndCutAllowed = SecurityStatus.isRecordingRestrictionsAllowed();
		const isDailyCutSet = Boolean(this.breakModel.cutExceedingDaily && this.breakModel.maximumWorkDurationDaily);
		const isStartOrEndCutSet = isStartOrEndCutAllowed && (this.isCutFlexTimeSet() || this.isCutNormalWorkingTimeSet());

		const hasDailyCutChanged = isDailyCutSet && (this.hasMaximumWorkDurationDailyChanged() || this.hasCutExceedingDailyChanged());
		const hasStartOrEndCutChanged = isStartOrEndCutAllowed &&
			(this.hasFlexTimeChanged() && this.isCutFlexTimeSet() || this.hasCutFlexTimeOrNormalWorkingTimeOrDailyChanged());
		// the cut handling decides whether a cut creates a validation, so the validations of already cut times have to be re-evaluated
		const hasCutHandlingOfActiveCutChanged = this.hasCutHandlingChanged() && (isDailyCutSet || isStartOrEndCutSet);

		return hasDailyCutChanged || hasStartOrEndCutChanged || hasCutHandlingOfActiveCutChanged;
	}

	private serializeBreakModelForPost(): JQuerySerializeArrayElement[] {
		const breakModel = copyOfObject(this.breakModel);
		clearInactiveFixedBreakFields(breakModel);
		return serializeObjectToArray({...breakModel});
	}

	private checkAndSaveBreakModel = () => {
		const button = this.breakModelDialog.getButtons("btn-primary");
		Button.setLoading(button);
		if (this.breakModel.id &&
			(this.observer.hasChanged() || this.hasVariableBreakCheckboxChanged || this.hasFixedBreakChanged || this.hasOvertimeLimitChanged || this.hasOvertimeRulesChanged() || this.hasCoreTime() !== this.hasInitiallyCoreTime || this.hasCutHandlingChanged())
		) {
			api("admin/checkIfBreakModelHasUnbalancedWorkingTimes.json")
				.post(this.serializeBreakModelForPost())
				.done(data => {
					if (data.foundWorkingTimes > 0) {
						let message = messages["dialog.update.breakModel.message"];
						const buttons: BootstrapModal.ModalButton[] = [{
							text: "dialog.update.breakModel.yes",
							css: "btn-danger",
							action: modal => {
								this.saveBreakModel([modal, this.breakModelDialog], false);
							}
						}, {
							text: "dialog.update.breakModel.no",
							css: "btn-primary",
							action: modal => {
								modal.close();
								this.breakModelDialog.resetLoadingButtons();
							}
						}];

						// If the break model has cuts changed or the maximum daily working time changed or the flex time period changed,
						// ask the user if he wants to apply them, but if cuts are not activated, don't ask (TIMR-7510)
						if (this.shouldAskToApplyCuts()) {
							message += "<br /><br /> " + messages["dialog.update.breakModel.message.applyCuts"];
							buttons.splice(1, 0, {
								text: "dialog.update.breakModel.yesApplyCuts",
								css: "btn-danger",
								action: (modal) => {
									this.saveBreakModel([modal, this.breakModelDialog], true);
								}
							});
						}

						new BootstrapModal.Modal({
							title: "dialog.update.breakModel.title",
							message: message,
							escapeText: false,
							buttons: buttons,
							size: "modal-lg",
							destroyOnClose: true
						}).show();
					} else {
						this.saveBreakModel([this.breakModelDialog], false);
					}
				})
				.fail((response: ErrorResponse) => {
					if (response.responseJSON?.fieldErrors) {
						this.markFieldErrors(response.responseJSON.fieldErrors);
					}
					Button.resetLoading(button);
				});
		} else {
			this.saveBreakModel([this.breakModelDialog], false);
		}
	}

	private saveBreakModel(modals: BootstrapModal.Modal[], applyCutsByFlexTimeOrNormalWorkingTimes: boolean) {
		this.clearAllValidationFeedback();

		const breakModelData: RequestParam[] = this.serializeBreakModelForPost();
		breakModelData.push({
			name: "applyCutsByFlexTimeOrNormalWorkingTimes",
			value: applyCutsByFlexTimeOrNormalWorkingTimes
		});

		const overtimeLimits: RequestParam[] = [];
		this.overtimeLimitPanel.getAssigned().forEach((overtimeLimit, index) => {
			const field = `overtimeLimits[${index}].`;
			overtimeLimits.push({
				name: field + "id",
				value: overtimeLimit.id
			}, {
				name: field + "duration",
				value: overtimeLimit.duration
			});
		});
		breakModelData.push(...overtimeLimits);

		if (SecurityStatus.isOvertimeAllowanceAllowed()) {
			const overtimeRules: RequestParam[] = [];
			$("#overtimeRuleList input[type=checkbox]:checked").each((index, elem) => {
				overtimeRules.push({
					name: `overtimeRules[${index}].id`,
					value: elem.getAttribute("value")
				});
			});
			breakModelData.push(...overtimeRules);
		}

		api("admin/saveBreakModel.json")
			.post(breakModelData)
			.done(() => {
				modals.forEach(modal => modal.close());
				$(this).triggerHandler(BreakModelsDialogHandler.EVENT_BREAK_MODEL_UPDATED);
			})
			.fail((response: ErrorResponse) => {
				if (response.responseJSON?.fieldErrors) {
					this.markFieldErrors(response.responseJSON.fieldErrors);
				}
			})
			.always(() => {
				modals.forEach(modal => Button.resetLoading(modal.getButtons("btn-primary")));
			});
	}

	/**
	 * Applies server-side validation errors to the hybrid break-model dialog.
	 *
	 * Most fields still use the legacy FormUtil feedback rendering. Fixed-break fields are handled
	 * by the React configuration panel instead, because the compact time-range UI has no usable
	 * space for inline error messages. We still ask FormUtil to mark the containing tab for
	 * fixed-break errors so users can find the invalid tab, but the actual field highlighting and
	 * message summary stay inside the React component.
	 */
	private markFieldErrors(fieldErrors: FieldError[]) {
		// Clone the backend errors before adapting field names for the dialog-specific validation state.
		this.validationState = fieldErrors.map(fieldError => ({...fieldError}));

		// Time-range validations are anchored on the start field by the backend, but the dialog
		// displays the feedback on the corresponding end field where users complete the range.
		this.validationState.forEach(error => {
			error.field = error.field.replace("flexTimeStart", "flexTimeEnd");
			error.field = error.field.replace("coreTime1Start", "coreTime1End");
			error.field = error.field.replace("coreTime2Start", "coreTime2End");
		});

		// Fixed-break and cut-handling fields are rendered by React panels; the remaining fields use standard (legacy) FormUtil feedback.
		const fixedBreakFieldErrors = this.validationState.filter(fieldError => isFixedBreakFieldError(fieldError));
		const cutHandlingFieldErrors = this.validationState.filter(fieldError => isCutHandlingFieldError(fieldError));
		const form = $("#breakModelForm");

		// Let the existing form feedback handle every field except the ones owned by a React panel.
		// Those panels need a compact React-specific rendering instead of inline FormUtil messages.
		FormUtil.markFieldErrors(form, this.validationState.filter(fieldError => !isFixedBreakFieldError(fieldError) && !isCutHandlingFieldError(fieldError)), true);

		// Update the React panels with only their own validation slice; all other errors stay in FormUtil.
		this.updateFixedBreakConfiguration();
		this.updateCutHandlingConfiguration();

		// Backend validation reports one side of a range; the UI marks both from/to inputs.
		fixedBreakFieldErrors.forEach((fieldError) => {
			getFixedBreakRangeFields(fieldError.field).forEach(field => {
				const input = this.findFieldInput(form, field);
				// Keep legacy tab error indicators working even though the field message is rendered by React.
				this.markFixedBreakTabFeedback(input, form);
			});
		});

		// Keep the legacy tab error indicator working even though the field message is rendered by React.
		if (cutHandlingFieldErrors.length > 0) {
			FormUtil.markTabFieldErrors($("#cutHandlingConfigurationPanel"), form);
		}
	}

	private findFieldInput(form: JQuery, field: string): JQuery {
		return form.find(`input[name="${field}"], select[name="${field}"], textarea[name="${field}"], [data-field="${field}"]`);
	}

	private markFixedBreakTabFeedback(input: JQuery, form: JQuery) {
		if (!input.length) {
			return;
		}
		FormUtil.markTabFieldErrors(input, form);
	}

	private clearAllValidationFeedback() {
		FormUtil.removeAllFeedback($("#breakModelForm"), true);
		this.validationState = [];
		this.updateFixedBreakConfiguration();
		this.updateCutHandlingConfiguration();
	}

	private clearFixedBreakErrors(unmarkBreaksTab = false) {
		this.validationState = this.validationState.filter(fieldError => !isFixedBreakFieldError(fieldError));
		if (unmarkBreaksTab && !FormUtil.hasFeedback($("#tabBreaks"))) {
			$('a[href="#tabBreaks"]').closest("li").removeClass("has-error").find("i").remove();
		}
		this.updateFixedBreakConfiguration();
	}

	private initListener() {
		this.resetOnUncheckCheckbox("#variableBreak1Checkbox", "variableBreak1BreakDuration", "variableBreak1WorkDuration");
		this.resetOnUncheckCheckbox("#variableBreak2Checkbox", "variableBreak2BreakDuration", "variableBreak2WorkDuration");
		this.resetOnUncheckCheckbox("#variableBreak3Checkbox", "variableBreak3BreakDuration", "variableBreak3WorkDuration");
		this.resetOnUncheckCheckbox("#dailyBreakCheckbox", "dailyBreakDuration", "dailyBreakWorkDuration");
		$("#validateFlexTimeCheckbox").change(() => {
			this.updateFlexTimeStatus($("#validateFlexTimeCheckbox").prop("checked") as boolean);
		});
		$("#validateCoreTimeCheckbox").change(() => {
			this.updateCoreTimeStatus($("#validateCoreTimeCheckbox").prop("checked") as boolean);
		});
		$(`select[name="flexTimeIndicatorType"]`).change(() => this.updateFlexTimeIndicatorsState());

		$("#dailyBreakCheckbox").on("change", (e) => {
			Input.slideToggle($("#dailyBreakCheckboxesPanel"), $(e.target).is(":checked"), 200);
		});

		$("#deductLegalMinimumBreaksCheckbox").change((e) => {
			const checked = $(e.target).is(":checked");
			if (!checked) {
				// Reset the fully deduct legal minimum breaks checkbox if the deduct legal minimum breaks checkbox is unchecked
				this.breakModel.fullyDeductLegalMinimumBreaks = false;
			}
		});

		this.observer.observeInput("#breakModelName", "name");

		this.observer.observeInput("#variableBreak1WorkDurationField", "variableBreak1WorkDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#variableBreak1BreakDurationField", "variableBreak1BreakDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#variableBreak2WorkDurationField", "variableBreak2WorkDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#variableBreak2BreakDurationField", "variableBreak2BreakDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#variableBreak3WorkDurationField", "variableBreak3WorkDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#variableBreak3BreakDurationField", "variableBreak3BreakDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#deductLegalMinimumBreaksCheckbox", "deductLegalMinimumBreaks", FieldObserver.getCheckboxState);
		this.observer.observeInput("#fullyDeductLegalMinimumBreaksCheckbox", "fullyDeductLegalMinimumBreaks", FieldObserver.getCheckboxState);

		this.observer.observeInput("#dailyBreakDurationField", "dailyBreakDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#dailyBreakWorkDurationField", "dailyBreakWorkDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#dailyBreakConsiderExistingBreaksCheckbox", "dailyBreakConsiderExistingBreaks", FieldObserver.getCheckboxState);
		this.observer.observeInput("#dailyBreakHardCutCheckbox", "dailyBreakHardCut", FieldObserver.getCheckboxState);

		this.observer.observeInput("#minimumManualBreakDurationField", "minimumManualBreakDuration", FieldObserver.getDurationFromField);

		this.observer.observeInput("#paidBreakDurationField", "paidBreakDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#paidBreakWorkDurationField", "paidBreakWorkDuration", FieldObserver.getDurationFromField);

		this.observer.observeInput("#breakModelApplyToProjectTime", "applyToProjectTime", FieldObserver.getCheckboxState);
		this.observer.observeInput("#maximumWorkDurationDailyField", "maximumWorkDurationDaily", FieldObserver.getDurationFromField);
		this.observer.observeInput("#maximumWorkDurationWeeklyField", "maximumWorkDurationWeekly", FieldObserver.getDurationFromField);
		this.observer.observeInput("#cutExceedingDailyCheckbox", "cutExceedingDaily", FieldObserver.getCheckboxState);

		this.dayLabels.forEach((day: "Monday" | "Tuesday" | "Wednesday" | "Thursday" | "Friday" | "Saturday" | "Sunday") => {
			// @ts-expect-error Dynamic typed modelField validation not working
			this.observer.observeInput("#flexTimeStart" + day, "flexTimeStart" + day, FieldObserver.getTimeField);
			// @ts-expect-error Dynamic typed modelField validation not working
			this.observer.observeInput("#flexTimeEnd" + day, "flexTimeEnd" + day, FieldObserver.getTimeField);
			// @ts-expect-error Dynamic typed modelField validation not working
			this.observer.observeInput("#coreTime1Start" + day, "coreTime1Start" + day, FieldObserver.getTimeField);
			// @ts-expect-error Dynamic typed modelField validation not working
			this.observer.observeInput("#coreTime1End" + day, "coreTime1End" + day, FieldObserver.getTimeField);
			// @ts-expect-error Dynamic typed modelField validation not working
			this.observer.observeInput("#coreTime2Start" + day, "coreTime2Start" + day, FieldObserver.getTimeField);
			// @ts-expect-error Dynamic typed modelField validation not working
			this.observer.observeInput("#coreTime2End" + day, "coreTime2End" + day, FieldObserver.getTimeField);
		});

		this.observer.onFieldValueChange((event, data) => {
			// Don't update the field values if the dailyBreakConsiderExistingBreaks or dailBreakHardCut fields have changed
			// because otherwise the UI can be collapsed and the user has to expand it again
			if (data.field === "dailyBreakConsiderExistingBreaks" || data.field === "dailyBreakHardCut") {
				return;
			}
			this.updateFieldValues();

			if (isMondayTimeFieldChange(data)) {
				this.autoFillWeekFlexCoreTimes(data);
			}
			if (data.field.match("^cut|flexTime")) {
				this.updateApplyCutsToProjectTimeChangeableState();
			}
			this.toggleFlexTimeMode(this.breakModel.flexTimeEnabled);
		});

		this.observer.observeInput("#validateDailyRestPeriod", "validateDailyRestPeriod");
		this.observer.observeInput("#dailyRestPeriodDuration", "validateDailyRestPeriodDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("input[type=radio][name=weeklyRestPeriodValidationStrategy]", "validateWeeklyRestPeriodStrategy", FieldObserver.getEnumValueFromRadio(WeeklyRestPeriodStrategyType));
		this.observer.observeInput("#weeklyRestPeriodStart", "validateWeeklyRestPeriodStart", FieldObserver.getTimeField);
		this.observer.observeInput("#validateHolidayRestPeriod", "validateHolidayRestPeriod");
		this.observer.observeInput("#holidayRestPeriodStart", "validateHolidayRestPeriodStart", FieldObserver.getTimeField);

		this.observer.observeInput("#cutBeforeNormalWorkingTimeStartsCheckbox", "cutBeforeNormalWorkingTimeStarts", FieldObserver.getCheckboxState);
		this.observer.observeInput("#cutAfterNormalWorkingTimeEndsCheckbox", "cutAfterNormalWorkingTimeEnds", FieldObserver.getCheckboxState);
		this.observer.observeInput("#cutBeforeFlexTimeStartsCheckbox", "cutBeforeFlexTimeStarts", FieldObserver.getCheckboxState);
		this.observer.observeInput("#cutAfterFlexTimeEndsCheckbox", "cutAfterFlexTimeEnds", FieldObserver.getCheckboxState);
		this.observer.observeInput("#applyCutsToProjectTimeCheckbox", "applyCutsToProjectTime", FieldObserver.getCheckboxState);
		this.observer.observeInput("#flexTimeCheckbox", "flexTimeEnabled", FieldObserver.getCheckboxState);

		// automatic overtime capping => NONE if checkbox is not checked else use the value from the select
		this.observer.observeInput("#automaticOvertimeCappingDurationField", "automaticOvertimeCappingDuration", FieldObserver.getDurationFromField);
		this.observer.observeInput("#automaticOvertimeCappingCheckbox, #automaticOvertimeCappingPeriodSelect", "automaticOvertimeCappingPeriod", () => {
			const selectedPeriod = $("#automaticOvertimeCappingPeriodSelect").val() as AutomaticOvertimeCappingPeriod;
			return $("#automaticOvertimeCappingCheckbox").is(":checked") ? selectedPeriod : AutomaticOvertimeCappingPeriod.NONE;
		});

		$(`input[name="flexTimeIndicatorYellowEmailAdministrator"]`).each((i, e) => {
			this.observer.observeInput(`input[name="${e.getAttribute("name")}"]:eq(${i})`, "flexTimeIndicatorYellowEmailAdministrator", FieldObserver.getCheckboxState, true)
		});
		$(`input[name="flexTimeIndicatorYellowEmailTeamLeader"]`).each((i, e) => {
			this.observer.observeInput(`input[name="${e.getAttribute("name")}"]:eq(${i})`, "flexTimeIndicatorYellowEmailTeamLeader", FieldObserver.getCheckboxState, true)
		});
		$(`input[name="flexTimeIndicatorYellowEmailUser"]`).each((i, e) => {
			this.observer.observeInput(`input[name="${e.getAttribute("name")}"]:eq(${i})`, "flexTimeIndicatorYellowEmailUser", FieldObserver.getCheckboxState, true)
		});
		$(`input[name="flexTimeIndicatorRedEmailAdministrator"]`).each((i, e) => {
			this.observer.observeInput(`input[name="${e.getAttribute("name")}"]:eq(${i})`, "flexTimeIndicatorRedEmailAdministrator", FieldObserver.getCheckboxState, true)
		});
		$(`input[name="flexTimeIndicatorRedEmailTeamLeader"]`).each((i, e) => {
			this.observer.observeInput(`input[name="${e.getAttribute("name")}"]:eq(${i})`, "flexTimeIndicatorRedEmailTeamLeader", FieldObserver.getCheckboxState, true)
		});
		$(`input[name="flexTimeIndicatorRedEmailUser"]`).each((i, e) => {
			this.observer.observeInput(`input[name="${e.getAttribute("name")}"]:eq(${i})`, "flexTimeIndicatorRedEmailUser", FieldObserver.getCheckboxState, true)
		});

		// Prevent set changed flag, as it should not recalculate all the working times
		this.observer.observeInput(`input[name="flexTimeIndicatorPositiveYellowThresholdDuration"]`, "flexTimeIndicatorPositiveYellowThresholdDuration", this.extractYellowThreshold("Positive"), true);
		this.observer.observeInput(`input[name="flexTimeIndicatorPositiveRedThresholdDuration"]`, "flexTimeIndicatorPositiveRedThresholdDuration", this.extractRedThreshold("Positive"), true, true);
		this.observer.observeInput(`input[name="flexTimeIndicatorPositiveYellowThresholdPercentage"]`, "flexTimeIndicatorPositiveYellowThresholdPercentage", FieldObserver.getPercentageFromField, true);
		this.observer.observeInput(`input[name="flexTimeIndicatorPositiveRedThresholdPercentage"]`, "flexTimeIndicatorPositiveRedThresholdPercentage", FieldObserver.getPercentageFromField, true);
		this.observer.observeInput(`input[name="flexTimeIndicatorNegativeYellowThresholdDuration"]`, "flexTimeIndicatorNegativeYellowThresholdDuration", this.extractYellowThreshold("Negative"), true);
		this.observer.observeInput(`input[name="flexTimeIndicatorNegativeRedThresholdDuration"]`, "flexTimeIndicatorNegativeRedThresholdDuration", this.extractRedThreshold("Negative"), true, true);
		this.observer.observeInput(`input[name="flexTimeIndicatorNegativeYellowThresholdPercentage"]`, "flexTimeIndicatorNegativeYellowThresholdPercentage", FieldObserver.getPercentageFromField, true);
		this.observer.observeInput(`input[name="flexTimeIndicatorNegativeRedThresholdPercentage"]`, "flexTimeIndicatorNegativeRedThresholdPercentage", FieldObserver.getPercentageFromField, true);
		this.observer.observeInput(`select[name="flexTimeIndicatorType"]`, "flexTimeIndicatorType", null, true);

		const onOvertimeLimitChange = () => {
			this.hasOvertimeLimitChanged = true;
		};
		this.overtimeLimitPanel.onAssignment(onOvertimeLimitChange);
		this.overtimeLimitPanel.onUnassignment(onOvertimeLimitChange);
	}

	private extractYellowThreshold(type: "Positive" | "Negative"): (selector: string) => number {
		return (selector: string) => {
			const yellowThreshold = FieldObserver.getNullableDurationFromField(selector);
			const redThreshold = this.breakModel[`flexTimeIndicator${type}RedThresholdDuration`];
			if (yellowThreshold == null && redThreshold == null) {
				this.breakModel[`flexTimeIndicator${type}RedThresholdDuration`] = 0;
			} else if (yellowThreshold != null && redThreshold === 0) {
				this.breakModel[`flexTimeIndicator${type}RedThresholdDuration`] = null;
			}
			return yellowThreshold;
		}
	}

	private extractRedThreshold(type: "Positive" | "Negative"): (selector: string) => number {
		return (selector: string) => {
			const redThreshold = FieldObserver.getNullableDurationFromField(selector);
			const yellowThreshold = this.breakModel[`flexTimeIndicator${type}YellowThresholdDuration`];
			if (yellowThreshold != null || redThreshold != null) {
				return redThreshold;
			} else {
				return 0;
			}
		}
	}

	private updateFieldValues() {
		this.breakModelDialog.toggleButton("btn-outline-danger", this.breakModel.id != null);
		$("#breakModelName").val(this.breakModel.name);

		FieldObserver.setDurationFieldWithEmptyOnZero("#variableBreak1WorkDurationField", this.breakModel.variableBreak1WorkDuration);
		FieldObserver.setDurationFieldWithEmptyOnZero("#variableBreak1BreakDurationField", this.breakModel.variableBreak1BreakDuration);
		$("#variableBreak1Checkbox").prop("checked", this.breakModel.variableBreak1BreakDuration || this.breakModel.variableBreak1WorkDuration);

		FieldObserver.setDurationFieldWithEmptyOnZero("#variableBreak2WorkDurationField", this.breakModel.variableBreak2WorkDuration);
		FieldObserver.setDurationFieldWithEmptyOnZero("#variableBreak2BreakDurationField", this.breakModel.variableBreak2BreakDuration);
		$("#variableBreak2Checkbox").prop("checked", this.breakModel.variableBreak2BreakDuration || this.breakModel.variableBreak2WorkDuration);

		FieldObserver.setDurationFieldWithEmptyOnZero("#variableBreak3WorkDurationField", this.breakModel.variableBreak3WorkDuration);
		FieldObserver.setDurationFieldWithEmptyOnZero("#variableBreak3BreakDurationField", this.breakModel.variableBreak3BreakDuration);
		$("#variableBreak3Checkbox").prop("checked", this.breakModel.variableBreak3BreakDuration || this.breakModel.variableBreak3WorkDuration);

		$("#deductLegalMinimumBreaksCheckbox").prop("checked", this.breakModel.deductLegalMinimumBreaks);
		$("#fullyDeductLegalMinimumBreaksCheckbox").prop("checked", this.breakModel.fullyDeductLegalMinimumBreaks);
		$("#fullyDeductLegalMinimumBreaksCheckboxContainer").slideToggleBool(this.breakModel.deductLegalMinimumBreaks);

		this.updateFixedBreakConfiguration();
		this.updateCutHandlingConfiguration();

		FieldObserver.setDurationFieldWithEmptyOnZero("#minimumManualBreakDurationField", this.breakModel.minimumManualBreakDuration);

		$("#breakModelApplyToProjectTime").prop("checked", this.breakModel.applyToProjectTime);

		FieldObserver.setDurationFieldWithEmptyOnZero("#paidBreakDurationField", this.breakModel.paidBreakDuration);
		FieldObserver.setDurationFieldWithEmptyOnZero("#paidBreakWorkDurationField", this.breakModel.paidBreakWorkDuration);

		FieldObserver.setDurationFieldWithEmptyOnZero("#dailyBreakDurationField", this.breakModel.dailyBreakDuration);
		FieldObserver.setDurationFieldWithEmptyOnZero("#dailyBreakWorkDurationField", this.breakModel.dailyBreakWorkDuration);
		$("#dailyBreakCheckbox").prop("checked", this.breakModel.dailyBreakDuration || this.breakModel.dailyBreakWorkDuration);
		Input.slideToggle($("#dailyBreakCheckboxesPanel"), $("#dailyBreakCheckbox").is(":checked"), 200);
		$("#dailyBreakConsiderExistingBreaksCheckbox").prop("checked", this.breakModel.dailyBreakConsiderExistingBreaks);
		$("#dailyBreakHardCutCheckbox").prop("checked", this.breakModel.dailyBreakHardCut);

		FieldObserver.setDurationFieldWithEmptyOnZero("#maximumWorkDurationDailyField", this.breakModel.maximumWorkDurationDaily);
		FieldObserver.setDurationFieldWithEmptyOnZero("#maximumWorkDurationWeeklyField", this.breakModel.maximumWorkDurationWeekly);
		$("#cutExceedingDailyCheckbox").prop("checked", this.breakModel.cutExceedingDaily);

		$("#cutBeforeNormalWorkingTimeStartsCheckbox").prop("checked", this.breakModel.cutBeforeNormalWorkingTimeStarts);
		$("#cutAfterNormalWorkingTimeEndsCheckbox").prop("checked", this.breakModel.cutAfterNormalWorkingTimeEnds);
		$("#cutBeforeFlexTimeStartsCheckbox").prop("checked", this.breakModel.cutBeforeFlexTimeStarts);
		$("#cutAfterFlexTimeEndsCheckbox").prop("checked", this.breakModel.cutAfterFlexTimeEnds);
		$("#applyCutsToProjectTimeCheckbox").prop("checked", this.breakModel.applyCutsToProjectTime);
		$("#flexTimeCheckbox").prop("checked", this.breakModel.flexTimeEnabled);
		this.dayLabels.forEach(day => {
			FieldObserver.setTimeField("#flexTimeStart" + day, this.breakModel[`flexTimeStart${day}`] ?? "");
			FieldObserver.setTimeField("#flexTimeEnd" + day, this.breakModel[`flexTimeEnd${day}`] ?? "");
			FieldObserver.setTimeField("#coreTime1Start" + day, this.breakModel[`coreTime1Start${day}`] ?? "");
			FieldObserver.setTimeField("#coreTime1End" + day, this.breakModel[`coreTime1End${day}`] ?? "");
			FieldObserver.setTimeField("#coreTime2Start" + day, this.breakModel[`coreTime2Start${day}`] ?? "");
			FieldObserver.setTimeField("#coreTime2End" + day, this.breakModel[`coreTime2End${day}`] ?? "");
		});

		$("#validateDailyRestPeriod").prop("checked", this.breakModel.validateDailyRestPeriod === true);
		FieldObserver.setDurationFieldWithEmptyOnZero("#dailyRestPeriodDuration", this.breakModel.validateDailyRestPeriodDuration);
		$(`input:radio[name=weeklyRestPeriodValidationStrategy][value="${this.breakModel.validateWeeklyRestPeriodStrategy}"]`).prop("checked", true);
		FieldObserver.setTimeField("#weeklyRestPeriodStart", this.breakModel.validateWeeklyRestPeriodStart);
		$("#validateHolidayRestPeriod").prop("checked", this.breakModel.validateHolidayRestPeriod);
		FieldObserver.setTimeField("#holidayRestPeriodStart", this.breakModel.validateHolidayRestPeriodStart);

		$("#dailyRestPeriodDurationContainer").toggle(this.breakModel.validateDailyRestPeriod);
		$("#weeklyRestStartContainer").toggle(this.breakModel.validateWeeklyRestPeriodStrategy === WeeklyRestPeriodStrategyType.WEEKEND);
		$("#weeklyRestPeriodSundayInfo").toggle(this.breakModel.validateWeeklyRestPeriodStrategy === WeeklyRestPeriodStrategyType.SUNDAY);
		$("#holidayRestStartContainer").toggle(this.breakModel.validateHolidayRestPeriod);

		FieldObserver.setDurationField("#automaticOvertimeCappingDurationField", this.breakModel.automaticOvertimeCappingDuration);
		$("#automaticOvertimeCappingCheckbox").prop("checked", this.breakModel.automaticOvertimeCappingPeriod != AutomaticOvertimeCappingPeriod.NONE);

		FieldObserver.setDurationField(`input[name="flexTimeIndicatorPositiveRedThresholdDuration"]`, this.breakModel.flexTimeIndicatorPositiveRedThresholdDuration == null && this.breakModel.flexTimeIndicatorPositiveYellowThresholdDuration == null ? 0 : this.breakModel.flexTimeIndicatorPositiveRedThresholdDuration);
		FieldObserver.setDurationField(`input[name="flexTimeIndicatorPositiveYellowThresholdDuration"]`, this.breakModel.flexTimeIndicatorPositiveYellowThresholdDuration);
		FieldObserver.setPercentageField(`input[name="flexTimeIndicatorPositiveRedThresholdPercentage"]`, this.breakModel.flexTimeIndicatorPositiveRedThresholdPercentage);
		FieldObserver.setPercentageField(`input[name="flexTimeIndicatorPositiveYellowThresholdPercentage"]`, this.breakModel.flexTimeIndicatorPositiveYellowThresholdPercentage);

		FieldObserver.setDurationField(`input[name="flexTimeIndicatorNegativeRedThresholdDuration"]`, this.breakModel.flexTimeIndicatorNegativeRedThresholdDuration == null && this.breakModel.flexTimeIndicatorNegativeYellowThresholdDuration == null ? 0 : this.breakModel.flexTimeIndicatorNegativeRedThresholdDuration);
		FieldObserver.setDurationField(`input[name="flexTimeIndicatorNegativeYellowThresholdDuration"]`, this.breakModel.flexTimeIndicatorNegativeYellowThresholdDuration);
		FieldObserver.setPercentageField(`input[name="flexTimeIndicatorNegativeRedThresholdPercentage"]`, this.breakModel.flexTimeIndicatorNegativeRedThresholdPercentage);
		FieldObserver.setPercentageField(`input[name="flexTimeIndicatorNegativeYellowThresholdPercentage"]`, this.breakModel.flexTimeIndicatorNegativeYellowThresholdPercentage);

		$(`input[name="flexTimeIndicatorYellowEmailAdministrator"]`).prop("checked", this.breakModel.flexTimeIndicatorYellowEmailAdministrator);
		$(`input[name="flexTimeIndicatorYellowEmailTeamLeader"]`).prop("checked", this.breakModel.flexTimeIndicatorYellowEmailTeamLeader);
		$(`input[name="flexTimeIndicatorYellowEmailUser"]`).prop("checked", this.breakModel.flexTimeIndicatorYellowEmailUser);
		$(`input[name="flexTimeIndicatorRedEmailAdministrator"]`).prop("checked", this.breakModel.flexTimeIndicatorRedEmailAdministrator);
		$(`input[name="flexTimeIndicatorRedEmailTeamLeader"]`).prop("checked", this.breakModel.flexTimeIndicatorRedEmailTeamLeader);
		$(`input[name="flexTimeIndicatorRedEmailUser"]`).prop("checked", this.breakModel.flexTimeIndicatorRedEmailUser);
		$(`select[name="flexTimeIndicatorType"]`).val(this.breakModel.flexTimeIndicatorType);

		this.updateCoreTimeCheckbox();
		this.updateDeductLegalMinimumBreakInfo();
		this.updateFlexTimeIndicatorsState();

		this.overtimeLimitPanel.updateView();

	}

	/**
	 * applyCutsToProjectTimeCheckbox is only changeable if one of the three visible "cut" checkboxes is checked
	 * if flexTime: cutExceedingDaily or cutBeforeFlexTimeStarts or cutAfterFlexTimeEnds
	 * if not flexTime: cutExceedingDaily or cutBeforeNormalWorkingTimeStarts or cutAfterNormalWorkingTimeEnds
	 * @private
	 */
	private updateApplyCutsToProjectTimeChangeableState() {
		const checked = this.breakModel.cutExceedingDaily || (this.breakModel.flexTimeEnabled ? this.breakModel.cutBeforeFlexTimeStarts || this.breakModel.cutAfterFlexTimeEnds :
			this.breakModel.cutBeforeNormalWorkingTimeStarts || this.breakModel.cutAfterNormalWorkingTimeEnds);

		const applyCutsToProjectTime = $("#applyCutsToProjectTimeCheckbox").prop("disabled", !checked);
		if (!checked) {
			this.breakModel.applyCutsToProjectTime = false;
			applyCutsToProjectTime.prop("checked", this.breakModel.applyCutsToProjectTime);
		}
	}

	/**
	 * Toggle if checkbox for automatic overtime capping is checked
	 * @private
	 */
	private toggleAutomaticOvertimeCappingCheckbox() {
		const automaticOvertimeCappingCheckbox = $("#automaticOvertimeCappingCheckbox");
		automaticOvertimeCappingCheckbox.prop("checked", this.breakModel.automaticOvertimeCappingPeriod !== AutomaticOvertimeCappingPeriod.NONE);
	}

	/**
	 * Toggles the checkbox containers based on the flexTime flag
	 * If flexTime is not active, the tab "Flex time & core time" is disabled
	 * @param flexTimeActive
	 * @private
	 */
	private toggleFlexTimeMode(flexTimeActive: boolean) {
		toggleNavigationTab("#liFlexTimeTab", !flexTimeActive);
		$("#cutFlexTimeWorkingTimeContainer").toggle(flexTimeActive);
		$("#cutNormalWorkingTimeContainer").toggle(!flexTimeActive);
	}

	private updateCutsTabAccess() {
		toggleNavigationTab("#liCutsTab", !SecurityStatus.isRecordingRestrictionsAllowed());
	}

	private autoFillWeekFlexCoreTimes(data: FieldValueChangeEventInfo<BreakModel, `${TimeFieldPrefix}Monday`>) {
		// if the entire "day line" is blank and the user fills in the Mo field -> Tu - Fr will also be filled with the same value
		const workDayLabels = this.dayLabels.filter(day => day !== "Monday" && day !== "Saturday" && day !== "Sunday");

		// TS cannot follow literal types through string surgery; TimeFieldPrefix restores what replace() erases.
		const fieldType = data.field.replace("Monday", "") as TimeFieldPrefix;
		if (!this.dayLabels.filter(day => day !== "Monday").some(day => this.breakModel[`${fieldType}${day}`])) {
			workDayLabels.forEach(day => {
				const fieldTypeAndDay = `${fieldType}${day}` as const;
				this.breakModel[fieldTypeAndDay] = data.newValue;
				FieldObserver.setTimeField("#" + fieldTypeAndDay, data.newValue);
			});
		}
	}

	private updateFlexTimeStatus(enabled: boolean) {
		if (!enabled) {
			this.dayLabels.forEach(day => {
				this.breakModel[`flexTimeStart${day}`] = null;
				this.breakModel[`flexTimeEnd${day}`] = null;
			});
			FormUtil.removeAllFeedback($("#flexTimeForm"), false);
			if (!FormUtil.hasFeedback($("#tabFlexiTime"))) {
				$("#liFlexTimeTab.has-error").removeClass("has-error").find("i").remove();
			}
			this.updateFieldValues();
		}
	}

	private updateCoreTimeStatus(enabled: boolean) {
		if (!enabled) {
			this.dayLabels.forEach(day => {
				this.breakModel[`coreTime1Start${day}`] = null;
				this.breakModel[`coreTime1End${day}`] = null;
				this.breakModel[`coreTime2Start${day}`] = null;
				this.breakModel[`coreTime2End${day}`] = null;
			});
			FormUtil.removeAllFeedback($("#coreTime1Form"), false);
			FormUtil.removeAllFeedback($("#coreTime2Form"), false);
			if (!FormUtil.hasFeedback($("#tabFlexiTime"))) {
				$("#liFlexTimeTab.has-error").removeClass("has-error").find("i").remove();
			}
			this.updateFieldValues();
		}
	}

	private updateDeductLegalMinimumBreakInfo() {
		const checked = $("#fullyDeductLegalMinimumBreaksCheckbox").is(":checked");
		const deductLegalMinimumBreaksInfo = $("#deductLegalMinimumBreaksInfo");
		if (checked) {
			deductLegalMinimumBreaksInfo.html(`<i class="fal fa-info-circle position-left" aria-hidden="true"></i>${messages["breakModel.fullyDeductLegalMinimumBreaks.info"]}`);
		} else {
			deductLegalMinimumBreaksInfo.html(`<i class="fal fa-info-circle position-left" aria-hidden="true"></i>${messages["breakModel.deductLegalMinimumBreaks.info"]}`);
		}
	}

	private resetOnUncheckCheckbox(selector: string, ...fields: (keyof BreakModel)[]) {
		$(selector).change(() => {
			if (!$(selector).prop("checked")) {
				fields.forEach(field => {
					// set hasVariableBreakCheckboxChanged true, when it has a corresponding field with a value different null
					if (!this.hasVariableBreakCheckboxChanged && this.breakModel[field] !== null) {
						this.hasVariableBreakCheckboxChanged = true;
					}
					// @ts-expect-error indexed property access not possible
					this.breakModel[field] = null;
				});
				this.updateFieldValues();
			}
		});
	}

	private getFixedBreakConfiguration(): FixedBreakConfiguration {
		return {
			fixedBreakType: this.breakModel.fixedBreakType,
			fixedBreak: this.breakModel.fixedBreak,
			fixedBreakMonday: this.breakModel.fixedBreakMonday,
			fixedBreakTuesday: this.breakModel.fixedBreakTuesday,
			fixedBreakWednesday: this.breakModel.fixedBreakWednesday,
			fixedBreakThursday: this.breakModel.fixedBreakThursday,
			fixedBreakFriday: this.breakModel.fixedBreakFriday,
			fixedBreakSaturday: this.breakModel.fixedBreakSaturday,
			fixedBreakSunday: this.breakModel.fixedBreakSunday
		};
	}

	/**
	 * Synchronizes the React fixed-break panel from the legacy dialog state.
	 * The surrounding break-model dialog still owns the persisted model and server validation state,
	 * so the React panel has to be updated explicitly whenever those legacy-side values change.
	 */
	private updateFixedBreakConfiguration() {
		this.fixedBreakConfigurationPanel.update(this.getFixedBreakConfiguration(), this.validationState.filter(fieldError => isFixedBreakFieldError(fieldError)));
	}

	/**
	 * Callback wired into the React {@link FixedBreakConfigurationPanelBridge} (see constructor).
	 * It bridges the panel's own state back into this page's `breakModel`, which is the single
	 * source of truth that ultimately gets persisted.
	 *
	 * Invoked whenever the user changes the fixed-break configuration in the panel. Besides
	 * copying the new values over, it keeps validation state and change tracking consistent:
	 * - On a change of the fixed-break *type* it discards now-obsolete validation errors.
	 * - On any actual value change it flags the form as dirty and refreshes the UI.
	 *
	 * DEVNOTE: This is intentionally not implemented as an imperative `bridge.getConfiguration()`
	 * call during save. That would still need a separate change callback for dirty tracking and
	 * immediate validation cleanup, splitting the same concern across two bridge APIs.
	 *
	 * Defined as an arrow-function property so `this` stays bound to the page when the function
	 * is handed to React as a callback.
	 *
	 * @param configuration the new fixed-break configuration emitted by the React panel
	 */
	private readonly onFixedBreakConfigurationChange = (configuration: FixedBreakConfiguration) => {
		// Snapshot the state *before* applying the change so unchanged blur commits do not mark the form dirty.
		const previousFixedBreakType = this.breakModel.fixedBreakType;
		const previousConfiguration = this.getFixedBreakConfiguration();

		// Copy the values from the React panel into the breakModel (type, global break and all seven weekdays).
		this.breakModel.fixedBreakType = configuration.fixedBreakType;
		this.breakModel.fixedBreak = configuration.fixedBreak;
		this.breakModel.fixedBreakMonday = configuration.fixedBreakMonday;
		this.breakModel.fixedBreakTuesday = configuration.fixedBreakTuesday;
		this.breakModel.fixedBreakWednesday = configuration.fixedBreakWednesday;
		this.breakModel.fixedBreakThursday = configuration.fixedBreakThursday;
		this.breakModel.fixedBreakFriday = configuration.fixedBreakFriday;
		this.breakModel.fixedBreakSaturday = configuration.fixedBreakSaturday;
		this.breakModel.fixedBreakSunday = configuration.fixedBreakSunday;

		// A change of the break type makes previous validation errors meaningless, so clear them
		// (and remove the error marker from the breaks tab if nothing else is wrong there).
		if (previousFixedBreakType !== this.breakModel.fixedBreakType) {
			this.clearFixedBreakErrors(true);
		}

		if (!fixedBreakConfigurationsEqual(previousConfiguration, this.getFixedBreakConfiguration())) {
			this.hasFixedBreakChanged = true;
			this.updateFieldValues();
		}
	}

	private getCutHandlingConfiguration(): CutHandlingConfiguration {
		return {
			cutHandlingMode: this.breakModel.cutHandlingMode,
			cutWarningThresholdDuration: this.breakModel.cutWarningThresholdDuration
		};
	}

	/** Synchronizes the React cut-handling panel from the legacy dialog state. */
	private updateCutHandlingConfiguration() {
		this.cutHandlingConfigurationPanel?.dispatchCommand("update", {
			configuration: this.getCutHandlingConfiguration(),
			fieldErrors: this.validationState.filter(fieldError => isCutHandlingFieldError(fieldError))
		});
	}

	private clearCutHandlingErrors() {
		this.validationState = this.validationState.filter(fieldError => !isCutHandlingFieldError(fieldError));
		if (!FormUtil.hasFeedback($("#tabCuts"))) {
			$('a[href="#tabCuts"]').closest("li").removeClass("has-error").find("i").remove();
		}
		this.updateCutHandlingConfiguration();
	}

	/** Callback wired into the React {@link mountCutHandlingConfigurationPanelIsland} (see constructor). */
	private readonly onCutHandlingConfigurationChange = (configuration: CutHandlingConfiguration) => {
		const previousConfiguration = this.getCutHandlingConfiguration();

		this.breakModel.cutHandlingMode = configuration.cutHandlingMode;
		this.breakModel.cutWarningThresholdDuration = configuration.cutWarningThresholdDuration;

		// A now-disabled threshold makes a stale "minutes required" error meaningless.
		if (configuration.cutWarningThresholdDuration === null) {
			this.clearCutHandlingErrors();
		}

		if (!cutHandlingConfigurationsEqual(previousConfiguration, configuration)) {
			this.updateCutHandlingConfiguration();
		}
	}

	private updateTitle(duplicate: boolean) {
		let message: string;
		if (!duplicate) {
			message = messages[this.breakModel.id == null ? "breakModel.header.create" : "breakModel.header.edit"];
		} else {
			message = messages["breakModel.header.duplicate"];
		}
		this.breakModelDialog.setTitle(message, true);
	}

	private updateCoreTimeCheckbox() {
		const isCoreTime = this.hasCoreTime();
		$("#validateCoreTimeCheckbox").prop("checked", isCoreTime);
	}

	private hasCoreTime(): boolean {
		return this.dayLabels.some(day => this.breakModel[`coreTime1Start${day}`]) ||
			this.dayLabels.some(day => this.breakModel[`coreTime1End${day}`]) ||
			this.dayLabels.some(day => this.breakModel[`coreTime2Start${day}`]) ||
			this.dayLabels.some(day => this.breakModel[`coreTime2End${day}`]);
	}

	private resizeAssignmentPanels() {
		const screenHeight = $(window).height();

		const panelHeight = Math.max(screenHeight - 450, 240);
		this.overtimeLimitPanel.setScrollHeight(panelHeight);
	}

	private getMaxGreenThreshold(indicatorType: FlexTimeIndicatorType): string {
		switch (indicatorType) {
			case FlexTimeIndicatorType.FIX_DURATION:
				return formatDuration(this.breakModel.flexTimeIndicatorPositiveYellowThresholdDuration || this.breakModel.flexTimeIndicatorPositiveRedThresholdDuration || 0) + "h";
			case FlexTimeIndicatorType.PERCENTAGE:
				return formatPercent(this.breakModel.flexTimeIndicatorPositiveYellowThresholdPercentage || this.breakModel.flexTimeIndicatorPositiveRedThresholdPercentage || 0, 0);
		}
	}

	private getMinGreenThreshold(indicatorType: FlexTimeIndicatorType): string {
		switch (indicatorType) {
			case FlexTimeIndicatorType.FIX_DURATION:
				return formatDuration(this.breakModel.flexTimeIndicatorNegativeYellowThresholdDuration || this.breakModel.flexTimeIndicatorNegativeRedThresholdDuration || 0) + "h";
			case FlexTimeIndicatorType.PERCENTAGE:
				return formatPercent(this.breakModel.flexTimeIndicatorNegativeYellowThresholdPercentage || this.breakModel.flexTimeIndicatorNegativeRedThresholdPercentage || 0, 0);
		}
	}
}


class BreakModelsTable extends BasicDataTable<BreakModel> {
	public static EVENT_BREAKMODEL_EDIT = "eventModelEdit";
	public static EVENT_BREAKMODEL_DUPLICATE = "eventModelDuplicate";
	public static EVENT_BREAKMODEL_DELETE = "eventModelDelete";

	constructor(config: BasicDataTableConfig) {
		super("/timr/admin/breakModels.json", config);
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt<"datatable-footer"ip>`,
			paging: true,
			serverSide: true,
			pageLength: Constants.ADMINISTRATION_MAIN_TABLES_PAGE_LENGTH
		};
	}

	protected createContextMenu() {
		return super.createContextMenu()
			.addMenuEntry({
				label: "breakModels.context.edit",
				action: (model) => {
					$(this).triggerHandler(BreakModelsTable.EVENT_BREAKMODEL_EDIT, model);
				}
			})
			.addMenuEntry({
				label: "breakModels.context.duplicate",
				action: (model) => {
					$(this).triggerHandler(BreakModelsTable.EVENT_BREAKMODEL_DUPLICATE, model);
				}
			})
			.addMenuEntry({
				label: "breakModels.context.delete",
				action: (model) => {
					$(this).triggerHandler(BreakModelsTable.EVENT_BREAKMODEL_DELETE, model);
				}
			});
	}

	protected getColumnSettings(): TypedColumnSettings<BreakModel>[] {
		return [
			{data: "id", width: "44px", orderable: false, className: "context-menu", render: this.contextMenu.render},
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "maximumWorkDurationDaily", orderable: false, render: BasicDataTable.renderDuration},
			{data: "maximumWorkDurationWeekly", orderable: false, render: BasicDataTable.renderDuration},
			{data: "id", orderable: false, render: this.renderBreakTimes},
			{
				data: "applyToProjectTime",
				orderable: false,
				render: this.renderApplyToProjectTime,
				visible: globals.projectTimeEnabled
			}
		];
	}

	private renderApplyToProjectTime(this: void, data: unknown, type: string, row: BreakModel, meta: DataTables.CellMetaSettings) {
		return row.applyToProjectTime ? `<i class="fal fa-check" title="${messages["breakModel.applyToProjectTime"]}"></i>` : "";
	}

	private renderBreakTimes(this: void, data: unknown, type: string, row: BreakModel, meta: DataTables.CellMetaSettings) {
		const breakInformation = [];

		function checkAndFormatVariable(breakDuration: number, workDuration: number) {
			if (breakDuration && workDuration) {
				breakInformation.push(messages.withArguments("admin.breakModel.breakTimes.variable", formatDuration(breakDuration), formatDuration(workDuration)));
			}
		}

		function formatFixedBreak(fixedBreak: FixedBreak): string[] {
			const fixedBreakInformation: string[] = [];
			if (fixedBreak?.from1 && fixedBreak?.to1) {
				fixedBreakInformation.push(messages.withArguments("admin.breakModel.breakTimes.fixed", formatTime(parseTimeInput(fixedBreak.from1)), formatTime(parseTimeInput(fixedBreak.to1))));
			}
			if (fixedBreak?.from2 && fixedBreak?.to2) {
				fixedBreakInformation.push(messages.withArguments("admin.breakModel.breakTimes.fixed", formatTime(parseTimeInput(fixedBreak.from2)), formatTime(parseTimeInput(fixedBreak.to2))));
			}
			if (fixedBreak?.from3 && fixedBreak?.to3) {
				fixedBreakInformation.push(messages.withArguments("admin.breakModel.breakTimes.fixed", formatTime(parseTimeInput(fixedBreak.from3)), formatTime(parseTimeInput(fixedBreak.to3))));
			}
			return fixedBreakInformation;
		}

		function formatFixedBreakTypeLabel(fixedBreakType: FixedBreakType, fixedBreakInformation: string[] = []): string {
			const label = messages[`breakModel.fixedBreakType.${fixedBreakType}`];
			if (fixedBreakInformation.length === 0) {
				return label;
			}
			// Show the selected fixed-break mode visible in the table preview
			return `${label}: ${fixedBreakInformation.join(", ")}`;
		}

		checkAndFormatVariable(row.variableBreak1BreakDuration, row.variableBreak1WorkDuration);
		checkAndFormatVariable(row.variableBreak2BreakDuration, row.variableBreak2WorkDuration);
		checkAndFormatVariable(row.variableBreak3BreakDuration, row.variableBreak3WorkDuration);

		if (row.fixedBreakType === FixedBreakType.PER_DAY) {
			const perDayBreakInformation = Constants.WEEKDAYS
				.map(day => {
					const dayBreakInformation = formatFixedBreak(row[`fixedBreak${day}`]);
					if (dayBreakInformation.length === 0) {
						return null;
					}
					return `${messages[`admin.clientSettings.weekday.${day.toUpperCase()}`]}: ${dayBreakInformation.join(", ")}`;
				})
				// Keep only truthy day break descriptions; empty days are returned as null above.
				.filter(Boolean);
			// Per-day fixed breaks can make this administration table hard to scan.
			// Show exact day details only while the preview stays short.
			if (perDayBreakInformation.length <= 2) {
				breakInformation.push(formatFixedBreakTypeLabel(FixedBreakType.PER_DAY, perDayBreakInformation));
			} else {
				breakInformation.push(`${messages["breakModel.fixedBreakType.PER_DAY"]} (${messages["admin.breakModel.breakTimes.perDay.moreThanTwoDays"]})`);
			}
		} else if (row.fixedBreakType === FixedBreakType.ALL_DAYS_EQUAL) {
			breakInformation.push(formatFixedBreakTypeLabel(FixedBreakType.ALL_DAYS_EQUAL, formatFixedBreak(row.fixedBreak)));
		}
		return breakInformation.join(", ");
	}

	protected getDefaultOrder(): [number, string][] {
		return [[1, "asc"]];
	}
}

class OvertimeLimitPanel extends AssignmentPanel<OvertimeLimit, WorkingTimeType> {
	private assignedComponent: AssignmentPanelComponent;
	private selectComponent: AssignmentPanelComponent;
	private overviewComponent: AssignmentPanelComponent;

	public constructor(config: AssignmentPanelConfiguration<OvertimeLimit, WorkingTimeType>, private onDurationUpdated: () => void) {
		super(config);

		this.assignedComponent = this.createAssignedComponent(this.config.panelElement.find(".assigned-element"));
		this.selectComponent = this.createSelectComponent(this.config.panelElement.find(".select-element"));
		this.overviewComponent = this.createAssignmentOverviewTable(this.config.panelElement.find(".overview-element"));

		$(this.selectComponent).on(BaseAssignmentTable.EVENT_ENTRY_REMOVED, (event, workingTimeType: WorkingTimeType) => {
			const overtimeLimit = {
				id: workingTimeType.id,
				name: workingTimeType.name,
				duration: 0
			};
			this.assign(overtimeLimit);
		});

		$(this.assignedComponent).on(BaseAssignmentTable.EVENT_ENTRY_REMOVED, (event, overtimeLimit: OvertimeLimit) => {
			this.unassign(overtimeLimit);
		});
	}

	public filterSelection(filter: string) {
		this.selectComponent.searchOfflineData(filter);
		this.assignedComponent.searchOfflineData(filter);
		this.overviewComponent.searchOfflineData(filter);
	}

	protected updateEditView() {
		this.assignedComponent.reload();
		this.selectComponent.reload();
	}

	protected updateOverview() {
		this.overviewComponent.reload();
	}

	public setScrollHeight(height: number) {
		this.assignedComponent.setScrollHeight(height);
		this.selectComponent.setScrollHeight(height);
		this.overviewComponent.setScrollHeight(height);

		this.assignedComponent.redrawCurrentPage();
		this.selectComponent.redrawCurrentPage();
		this.overviewComponent.redrawCurrentPage();
	}

	protected getElementsForSelection(): PromiseLike<WorkingTimeType[]> {
		const allAssigned = this.getAssigned();

		return this.config.selectSourceProvider().then(workingTimeTypes => {
			return workingTimeTypes.filter(wtt => {
				return !allAssigned.some(a => a.id === wtt.id);
			});
		});
	}

	protected createAssignedComponent(element: JQuery): AssignmentPanelComponent {
		return new OvertimeLimitAssignedTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		}, () => this.onDurationUpdated());
	}

	protected createSelectComponent(element: JQuery): AssignmentPanelComponent {
		return new WorkingTimeTypeSelectTable(() => this.getElementsForSelection(), {
			element: this.config.panelElement.find("table.select-element"),
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}

	protected createAssignmentOverviewTable(element: JQuery): BasicDataTable<OvertimeLimit> {
		return new OvertimeLimitOverviewTable(() => this.getAssigned(), {
			element: element,
			orderable: false,
			allowExpand: false,
			clampNote: false
		});
	}
}

class OvertimeLimitAssignedTable extends BaseAssignmentTable<OvertimeLimit> {

	constructor(dataSource: DataTablesDataSource<OvertimeLimit>, config: BasicDataTableConfig, private onDurationUpdated: () => void) {
		super(dataSource, config);

		this.config.element.on("change", `input[name="duration"]`, (event) => {
			const input = $(event.target as HTMLElement);
			const overtimeLimit = this.getRowData(input);
			overtimeLimit.duration = parseDuration(input.val() as string) * 60000;

			this.onDurationUpdated();
		});
	}

	protected getColumnSettings(): TypedColumnSettings<OvertimeLimit>[] {
		return [
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "duration", orderable: true, render: this.renderDurationInput},
			{
				className: "no-padding-top no-padding-bottom no-padding-left",
				orderable: false,
				render: () => this.renderControlButton("changeAssignmentButton", messages["breakModel.overtimeLimit.remove"], "fa-minus"),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	private renderDurationInput(this: void, data: number, type: string, row: OvertimeLimit, meta: DataTables.CellMetaSettings) {
		return `<div class="input-group input-group-xs">
					<input type="text" name="duration" class="form-control" value="${formatDuration(data)}">
				</div>`;
	}
}

class WorkingTimeTypeSelectTable extends BaseAssignmentTable<WorkingTimeType> {
	protected getColumnSettings(): TypedColumnSettings<WorkingTimeType>[] {
		return [
			{
				orderable: false,
				className: "no-padding-top no-padding-bottom no-padding-right",
				render: () => this.renderControlButton("changeAssignmentButton", messages["breakModel.overtimeLimit.add"], "fa-plus"),
				width: BasicDataTable.COLUMN_SIZE_QUICK_SWITCH_BUTTON
			},
			{
				data: "name", orderable: true, render: BasicDataTable.renderText, searchable: true
			}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}
}

class OvertimeLimitOverviewTable extends BasicDataTable<OvertimeLimit> {
	protected getColumnSettings(): TypedColumnSettings<OvertimeLimit>[] {
		return [
			{data: "name", orderable: true, render: BasicDataTable.renderText},
			{data: "duration", orderable: true, render: BasicDataTable.renderDuration}
		];
	}

	protected getDefaultOrder(): (string | number)[][] {
		return [[0, "asc"]];
	}

	protected createDataTableConfig(): DataTables.Settings {
		return {
			dom: `rt`,
			paging: false,
			scrollY: "300px",
			scrollCollapse: true,
			searching: true
		};
	}
}
